/*
 * Copyright (c) 2024-2026 Nodals AI Ltd
 * All rights reserved.
 */

import type { DcfgDataType, DcfgTransform, XdValue } from '../types/collection';

/**
 * Applies the ordered transform list to a raw read value. Transforms are
 * type-tolerant pass-throughs: input of a non-matching type is returned
 * unchanged, so e.g. `first` + `split` behaves correctly whether the read
 * yielded a string[] or a bare string.
 */
const applyTransforms = (value: unknown, transforms?: DcfgTransform[]): unknown => {
  if (!Array.isArray(transforms)) {
    return value;
  }
  let current = value;
  for (const transform of transforms) {
    switch (transform.op) {
      case 'first':
        if (Array.isArray(current)) {
          current = current.length > 0 ? current[0] : undefined;
        }
        break;
      case 'split':
        if (typeof current === 'string') {
          current = current.split(transform.delimiter ?? ',').map((part) => part.trim());
        }
        break;
      case 'lowercase':
        if (typeof current === 'string') {
          current = current.toLowerCase();
        } else if (Array.isArray(current)) {
          current = current.map((item) => (typeof item === 'string' ? item.toLowerCase() : item));
        }
        break;
      case 'nth':
        if (Array.isArray(current)) {
          current =
            Number.isInteger(transform.index) && transform.index >= 0 && transform.index < current.length
              ? current[transform.index]
              : undefined;
        }
        break;
      // Unknown ops are ignored: forward-compatible with future transforms.
    }
    if (current === undefined || current === null) {
      return undefined;
    }
  }
  return current;
};

/** A value is only stringifiable if String() produces something meaningful. */
const toScalarString = (value: unknown): string | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }
  const valueType = typeof value;
  if (valueType === 'object' || valueType === 'function' || valueType === 'symbol') {
    return undefined;
  }
  return String(value);
};

/**
 * Coerces a transformed value to the data point's declared type. Returns
 * undefined ("missing") when coercion is not possible — missing is never an
 * error. Output shape is always string | string[] | number | boolean.
 */
const coerce = (value: unknown, dataType: DcfgDataType | undefined): XdValue | undefined => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (dataType === 'ARRAY') {
    const items = Array.isArray(value) ? value : [value];
    const coercedItems: string[] = [];
    for (const item of items) {
      const asString = toScalarString(item);
      if (asString !== undefined) {
        coercedItems.push(asString);
      }
    }
    return coercedItems.length > 0 ? coercedItems : undefined;
  }

  // Array → scalar type: implicit `first`; empty array ⇒ missing.
  let scalar = value;
  if (Array.isArray(scalar)) {
    if (scalar.length === 0) {
      return undefined;
    }
    scalar = scalar[0];
  }

  switch (dataType) {
    case 'STRING':
      return toScalarString(scalar);
    case 'INTEGER':
    case 'FLOAT': {
      if (toScalarString(scalar) === undefined) {
        return undefined;
      }
      const asNumber = Number(scalar);
      if (!Number.isFinite(asNumber)) {
        return undefined;
      }
      return dataType === 'INTEGER' ? Math.trunc(asNumber) : asNumber;
    }
    case 'BOOLEAN': {
      const asString = toScalarString(scalar);
      if (asString === undefined) {
        return undefined;
      }
      const normalized = asString.toLowerCase();
      if (normalized === 'true' || normalized === '1') {
        return true;
      }
      if (normalized === 'false' || normalized === '0') {
        return false;
      }
      return undefined;
    }
    default:
      return undefined;
  }
};

export { applyTransforms, coerce };
